import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  getAllCustomers,
  getCustomerTypes,
  getSalesExecutives,
} from '../services/customer_service'
import { getPrintBooks } from '../services/print_service'
import { generatePublishingReport } from '../services/publishing_report_service'
import './PublishingReport.css'

const reportTypes = [
  {
    value: 'summary',
    label: 'Summary',
    description: 'A concise overview with totals and key figures.',
  },
  {
    value: 'list',
    label: 'List',
    description: 'A detailed item-by-item report for review.',
  },
]

const periods = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This week' },
  { value: 'last-week', label: 'Last week' },
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'this-quarter', label: 'This quarter' },
  { value: 'this-year', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
]

const entities = [
  { value: 'orders', label: 'Orders', hint: 'Order activity and value' },
  {
    value: 'customers',
    label: 'Customers',
    hint: 'Customer accounts and activity',
  },
  { value: 'books', label: 'Books', hint: 'Titles, copies, and performance' },
  {
    value: 'sales-executive',
    label: 'Sales executive',
    hint: 'Sales performance by executive',
  },
  { value: 'debtors', label: 'Debtors', hint: 'Outstanding customer balances' },
]

const orderTypes = [
  { value: 'published_book', label: 'Published books' },
  { value: 'exercise_book', label: 'Exercise books' },
]

const getOptionValue = (option) =>
  typeof option === 'object'
    ? option.id ||
      option.value ||
      option.customerid ||
      option.customer_id ||
      option.userid ||
      option.user_id ||
      option.sales_executive_id ||
      option.sales_exec_id ||
      option.customertypeid ||
      option.customer_type_id ||
      option.type_id ||
      option.bookid ||
      option.book_id
    : option

const getOptionLabel = (option) =>
  typeof option === 'object'
    ? option.name ||
      option.label ||
      option.cust_name ||
      option.customer_name ||
      option.sales_exec_name ||
      option.sales_executive ||
      option.user_fullname ||
      option.fullname ||
      option.customertype ||
      option.customer_type ||
      option.book_title ||
      option.booktitle ||
      option.title ||
      getOptionValue(option)
    : option

const formatFieldLabel = (value) =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())

const formatReportValue = (value) => {
  if (value === null || value === undefined || value === '') return '-'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function SummaryData({ data }) {
  if (Array.isArray(data)) {
    if (!data.length) return <p>No records were returned for this report.</p>
    if (typeof data[0] !== 'object' || data[0] === null) {
      return <p>{data.map(formatReportValue).join(', ')}</p>
    }

    const columns = Array.from(
      new Set(data.flatMap((record) => Object.keys(record))),
    )
    return (
      <div className='generated-report__table-wrap'>
        <table className='generated-report__table'>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{formatFieldLabel(column)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((record, index) => (
              <tr key={record.id || record._id || index}>
                {columns.map((column) => (
                  <td key={column}>{formatReportValue(record[column])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  if (data && typeof data === 'object') {
    return (
      <dl className='generated-report__metrics'>
        {Object.entries(data).map(([key, value]) => (
          <div key={key}>
            <dt>{formatFieldLabel(key)}</dt>
            <dd>{formatReportValue(value)}</dd>
          </div>
        ))}
      </dl>
    )
  }

  return <p>{formatReportValue(data)}</p>
}

function PublishingReport() {
  const [reportType, setReportType] = useState('summary')
  const [period, setPeriod] = useState('this-month')
  const [entity, setEntity] = useState('orders')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [orderType, setOrderType] = useState('all')
  const [customerType, setCustomerType] = useState('all')
  const [customer, setCustomer] = useState('all')
  const [bookType, setBookType] = useState('all')
  const [book, setBook] = useState('all')
  const [salesExecutive, setSalesExecutive] = useState('all')
  const [customerTypes, setCustomerTypes] = useState([])
  const [customers, setCustomers] = useState([])
  const [books, setBooks] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [optionsError, setOptionsError] = useState('')
  const [optionsLoading, setOptionsLoading] = useState(true)
  const [customersLoading, setCustomersLoading] = useState(true)
  const [booksLoading, setBooksLoading] = useState(true)
  const [generatedReport, setGeneratedReport] = useState(null)
  const [validationError, setValidationError] = useState('')
  const [requestError, setRequestError] = useState('')
  const [downloadMessage, setDownloadMessage] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  useEffect(() => {
    let isMounted = true

    Promise.all([getCustomerTypes(), getSalesExecutives()])
      .then(([loadedCustomerTypes, loadedSalesExecutives]) => {
        if (!isMounted) return
        setCustomerTypes(loadedCustomerTypes)
        setSalesExecutives(loadedSalesExecutives)
      })
      .catch((requestError) => {
        if (isMounted) setOptionsError(requestError.message)
      })
      .finally(() => {
        if (isMounted) setOptionsLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    setCustomersLoading(true)
    setOptionsError('')

    getAllCustomers(customerType === 'all' ? '' : customerType)
      .then((loadedCustomers) => {
        if (isMounted) setCustomers(loadedCustomers)
      })
      .catch((requestError) => {
        if (isMounted) setOptionsError(requestError.message)
      })
      .finally(() => {
        if (isMounted) setCustomersLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [customerType])

  useEffect(() => {
    const controller = new AbortController()
    setBooksLoading(true)
    setOptionsError('')

    const booksRequest =
      bookType === 'all'
        ? Promise.all(
            orderTypes.map((type) =>
              getPrintBooks(type.value, controller.signal),
            ),
          ).then((lists) => {
            const uniqueBooks = new Map()
            lists.flat().forEach((item) => {
              uniqueBooks.set(String(getOptionValue(item)), item)
            })
            return [...uniqueBooks.values()]
          })
        : getPrintBooks(bookType, controller.signal)

    booksRequest
      .then(setBooks)
      .catch((requestError) => {
        if (requestError.name !== 'AbortError') {
          setOptionsError(requestError.message)
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBooksLoading(false)
      })

    return () => controller.abort()
  }, [bookType])

  const selectedPeriod = periods.find((item) => item.value === period)
  const selectedEntity = entities.find((item) => item.value === entity)
  const selectedType = reportTypes.find((item) => item.value === reportType)
  const customPeriodIncomplete = period === 'custom' && (!fromDate || !toDate)
  const selectedOrderType = orderTypes.find((item) => item.value === orderType)
  const selectedCustomer = customers.find(
    (item) => String(getOptionValue(item)) === customer,
  )
  const selectedCustomerType = customerTypes.find(
    (item) => String(getOptionValue(item)) === customerType,
  )
  const selectedBookType = orderTypes.find((item) => item.value === bookType)
  const selectedBook = books.find(
    (item) => String(getOptionValue(item)) === book,
  )
  const selectedSalesExecutive = salesExecutives.find(
    (item) => String(getOptionValue(item)) === salesExecutive,
  )

  const reportScope =
    entity === 'orders'
      ? selectedOrderType?.label || 'All order types'
      : entity === 'customers' && selectedCustomer
        ? `${getOptionLabel(selectedCustomerType) || 'All customer types'} · ${getOptionLabel(selectedCustomer)}`
        : entity === 'books' && selectedBook
          ? `${selectedBookType?.label || 'All book types'} · ${getOptionLabel(selectedBook)}`
          : (entity === 'sales-executive' || entity === 'debtors') &&
              selectedSalesExecutive
            ? getOptionLabel(selectedSalesExecutive)
            : entity === 'customers'
              ? `${getOptionLabel(selectedCustomerType) || 'All customer types'} · All customers`
              : entity === 'books'
                ? `${selectedBookType?.label || 'All book types'} · All books`
                : entity === 'sales-executive' || entity === 'debtors'
                  ? 'All sales executives'
                  : ''

  function getValidationError() {
    if (!reportType) return 'Choose a report type.'
    if (!period) return 'Choose a reporting period.'
    if (customPeriodIncomplete) return 'Choose both custom period dates.'
    if (fromDate && toDate && fromDate > toDate) {
      return 'The start date cannot be after the end date.'
    }
    if (!entity) return 'Choose an entity.'
    if (entity === 'orders' && !orderType) return 'Choose an order type.'
    if (entity === 'customers' && !customerType) {
      return 'Choose a customer type.'
    }
    if (entity === 'customers' && !customer) return 'Choose a customer.'
    if (entity === 'books' && !bookType) return 'Choose a book type.'
    if (entity === 'books' && !book) return 'Choose a book.'
    if (
      (entity === 'sales-executive' || entity === 'debtors') &&
      !salesExecutive
    ) {
      return 'Choose a sales executive.'
    }
    return ''
  }

  async function generateReport(event) {
    event.preventDefault()
    const message = getValidationError()
    setValidationError(message)
    setRequestError('')
    setDownloadMessage('')
    if (message) return

    setIsGenerating(true)
    try {
      const data = await generatePublishingReport({
        report_type: reportType,
        period,
        ...(period === 'custom'
          ? { from_date: fromDate, to_date: toDate }
          : {}),
        entity,
        ...(entity === 'orders' ? { order_type: orderType } : {}),
        ...(entity === 'customers'
          ? { customer_type: customerType, customer_id: customer }
          : {}),
        ...(entity === 'books' ? { book_type: bookType, book_id: book } : {}),
        ...(entity === 'sales-executive' || entity === 'debtors'
          ? { sales_executive_id: salesExecutive }
          : {}),
      })

      if (reportType === 'list') {
        setGeneratedReport(null)
        setDownloadMessage('The Excel report has been downloaded.')
      } else {
        setGeneratedReport({
          data,
          reportType: selectedType.label,
          period:
            period === 'custom'
              ? `${fromDate} to ${toDate}`
              : selectedPeriod.label,
          entity: selectedEntity.label,
          scope: reportScope,
          generatedAt: new Date().toLocaleString(),
        })
      }
    } catch (error) {
      setGeneratedReport(null)
      setRequestError(error.message)
    } finally {
      setIsGenerating(false)
    }
  }

  function chooseEntity(value) {
    setEntity(value)
    setValidationError('')
    setRequestError('')
    setDownloadMessage('')
    setGeneratedReport(null)
  }

  return (
    <div className='publishing-report-page'>
      <nav className='publishing-report__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <span>Reports</span>
        <span aria-hidden='true'>/</span>
        <strong>Publishing</strong>
      </nav>

      <header className='publishing-report__header'>
        <div>
          <span className='app-content__eyebrow'>Publishing reports</span>
          <h1>Build a publishing report</h1>
          <p>
            Select the level of detail, reporting period, and records to
            include.
          </p>
        </div>
        <div className='publishing-report__mark' aria-hidden='true'>
          <span>REPORT</span>
          <strong>01</strong>
        </div>
      </header>

      <form className='report-builder' onSubmit={generateReport}>
        <section
          className='report-builder__section'
          aria-labelledby='report-type-title'
        >
          <div className='report-builder__section-heading'>
            <span>01</span>
            <div>
              <h2 id='report-type-title'>Report type</h2>
              <p>Choose how much detail the report should contain.</p>
            </div>
          </div>
          <div className='report-type-options'>
            {reportTypes.map((type) => (
              <label
                className={`report-type-option ${reportType === type.value ? 'is-selected' : ''}`}
                key={type.value}
              >
                <input
                  type='radio'
                  name='reportType'
                  value={type.value}
                  checked={reportType === type.value}
                  onChange={(event) => setReportType(event.target.value)}
                />
                <span
                  className='report-type-option__indicator'
                  aria-hidden='true'
                />
                <span>
                  <strong>{type.label}</strong>
                  <small>{type.description}</small>
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='report-period-title'
        >
          <div className='report-builder__section-heading'>
            <span>02</span>
            <div>
              <h2 id='report-period-title'>Period</h2>
              <p>Use a common period or define a custom date range.</p>
            </div>
          </div>
          <div className='report-periods'>
            {periods.map((item) => (
              <button
                className={period === item.value ? 'is-selected' : ''}
                type='button'
                key={item.value}
                onClick={() => setPeriod(item.value)}
                aria-pressed={period === item.value}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === 'custom' && (
            <div className='report-custom-period'>
              <label>
                From
                <input
                  type='date'
                  value={fromDate}
                  max={toDate || undefined}
                  onChange={(event) => setFromDate(event.target.value)}
                  required
                />
              </label>
              <span aria-hidden='true'>to</span>
              <label>
                To
                <input
                  type='date'
                  value={toDate}
                  min={fromDate || undefined}
                  onChange={(event) => setToDate(event.target.value)}
                  required
                />
              </label>
            </div>
          )}
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='report-entity-title'
        >
          <div className='report-builder__section-heading'>
            <span>03</span>
            <div>
              <h2 id='report-entity-title'>Entity</h2>
              <p>Select the publishing records to report on.</p>
            </div>
          </div>
          <div className='report-entities'>
            {entities.map((item) => (
              <label
                className={`report-entity ${entity === item.value ? 'is-selected' : ''}`}
                key={item.value}
              >
                <input
                  type='radio'
                  name='entity'
                  value={item.value}
                  checked={entity === item.value}
                  onChange={(event) => chooseEntity(event.target.value)}
                />
                <span>
                  <strong>{item.label}</strong>
                  <small>{item.hint}</small>
                </span>
                <span className='report-entity__check' aria-hidden='true'>
                  ✓
                </span>
              </label>
            ))}
          </div>
        </section>

        <section
          className='report-builder__section'
          aria-labelledby='report-options-title'
        >
          <div className='report-builder__section-heading'>
            <span>04</span>
            <div>
              <h2 id='report-options-title'>Report options</h2>
              <p>
                Choose which {selectedEntity.label.toLowerCase()} to include.
              </p>
            </div>
          </div>
          <div className='report-entity-options'>
            {entity === 'orders' && (
              <label>
                Order type
                <select
                  value={orderType}
                  onChange={(event) => setOrderType(event.target.value)}
                >
                  <option value='all'>All order types</option>
                  {orderTypes.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {entity === 'customers' && (
              <>
                <label>
                  Customer type
                  <select
                    value={customerType}
                    onChange={(event) => {
                      setCustomerType(event.target.value)
                      setCustomer('all')
                    }}
                    disabled={optionsLoading}
                    required
                  >
                    <option value='all'>All customer types</option>
                    {customerTypes.map((item) => (
                      <option
                        key={getOptionValue(item)}
                        value={getOptionValue(item)}
                      >
                        {getOptionLabel(item)}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Customer
                  <select
                    value={customer}
                    onChange={(event) => setCustomer(event.target.value)}
                    disabled={customersLoading}
                    required
                  >
                    <option value='all'>
                      {customersLoading
                        ? 'Loading customers...'
                        : 'All customers'}
                    </option>
                    {customers.map((item) => (
                      <option
                        key={getOptionValue(item)}
                        value={getOptionValue(item)}
                      >
                        {getOptionLabel(item)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            {entity === 'books' && (
              <>
                <label>
                  Book type
                  <select
                    value={bookType}
                    onChange={(event) => {
                      setBookType(event.target.value)
                      setBook('all')
                    }}
                  >
                    <option value='all'>All book types</option>
                    {orderTypes.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Book
                  <select
                    value={book}
                    onChange={(event) => setBook(event.target.value)}
                    disabled={booksLoading}
                    required
                  >
                    <option value='all'>
                      {booksLoading ? 'Loading books...' : 'All books'}
                    </option>
                    {books.map((item) => (
                      <option
                        key={getOptionValue(item)}
                        value={getOptionValue(item)}
                      >
                        {getOptionLabel(item)}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            )}

            {(entity === 'sales-executive' || entity === 'debtors') && (
              <label>
                {entity === 'debtors'
                  ? 'Filter debtors by sales executive'
                  : 'Sales executive'}
                <select
                  value={salesExecutive}
                  onChange={(event) => setSalesExecutive(event.target.value)}
                  disabled={optionsLoading}
                  required
                >
                  <option value='all'>All sales executives</option>
                  {salesExecutives.map((item) => (
                    <option
                      key={getOptionValue(item)}
                      value={getOptionValue(item)}
                    >
                      {getOptionLabel(item)}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {optionsLoading &&
              (entity === 'sales-executive' || entity === 'debtors') && (
                <span className='report-entity-options__status'>
                  Loading options...
                </span>
              )}
            {optionsError && entity !== 'orders' && (
              <span className='report-entity-options__error' role='alert'>
                {optionsError}
              </span>
            )}
          </div>
        </section>

        {(validationError || requestError) && (
          <div className='report-builder__error' role='alert'>
            {validationError || requestError}
          </div>
        )}
        {downloadMessage && (
          <div className='report-builder__success' role='status'>
            {downloadMessage}
          </div>
        )}

        <footer className='report-builder__footer'>
          <div>
            <span>Ready to generate</span>
            <strong>
              {selectedType.label} · {selectedPeriod.label} ·{' '}
              {selectedEntity.label} · {reportScope}
            </strong>
          </div>
          <button type='submit' disabled={isGenerating}>
            {isGenerating
              ? 'Generating...'
              : reportType === 'list'
                ? 'Generate & download'
                : 'Generate report'}{' '}
            {!isGenerating && <span aria-hidden='true'>→</span>}
          </button>
        </footer>
      </form>

      {generatedReport && (
        <section className='generated-report' aria-live='polite'>
          <div>
            <span className='app-content__eyebrow'>Report ready</span>
            <h2>
              {generatedReport.entity} {generatedReport.reportType}
            </h2>
            <p>
              {generatedReport.scope} · {generatedReport.period} · Generated{' '}
              {generatedReport.generatedAt}
            </p>
            <SummaryData data={generatedReport.data} />
          </div>
          <button type='button' onClick={() => window.print()}>
            Print report
          </button>
        </section>
      )}
    </div>
  )
}

export default PublishingReport
